"use client";

import { useMemo } from "react";
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from "recharts";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { type ChartConfig, ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { useUsageBuckets } from "@/lib/usage/usage.hooks";

const chartConfig = {
  spend: {
    label: "Spend",
    color: "var(--chart-1)",
  },
} satisfies ChartConfig;

export const DashboardChartSpend = () => {
  const { data, isLoading } = useUsageBuckets({ count: 16 });
  const buckets = useMemo(() => data?.usage_buckets ?? [], [data]);

  const dateFormatter = useMemo(() => new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric" }), []);

  const daily = useMemo(
    () =>
      buckets.map((b) => ({
        date: dateFormatter.format(new Date(`${b.date}T00:00:00`)),
        spend: Number(b.cost_cents) / 100,
      })),
    [buckets, dateFormatter],
  );

  return (
    <Card className="mx-4 lg:mx-6">
      <CardHeader>
        <CardTitle>Daily spend</CardTitle>
        <CardDescription>Spend per day from your usage history.</CardDescription>
      </CardHeader>
      <CardContent className="pl-2">
        {isLoading ? (
          <p className="py-10 text-center text-sm text-muted-foreground">Loading...</p>
        ) : (
          <ChartContainer config={chartConfig} className="aspect-auto h-[240px] w-full">
            <BarChart accessibilityLayer data={daily} margin={{ left: 12, right: 12 }}>
              <CartesianGrid vertical={false} />
              <XAxis dataKey="date" tickLine={false} axisLine={false} tickMargin={8} minTickGap={24} />
              <YAxis
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                tickFormatter={(v) => `$${Number(v).toFixed(0)}`}
              />
              <ChartTooltip
                cursor={{ fill: "var(--muted)", opacity: 0.3 }}
                content={<ChartTooltipContent formatter={(v) => [`$${Number(v).toFixed(2)}`, "Spend"]} />}
              />
              <Bar dataKey="spend" fill="var(--color-spend)" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ChartContainer>
        )}
      </CardContent>
    </Card>
  );
};
